import { StButton } from "@storytell/ui/buttons";
import { clsx } from "clsx";
import { TbArchive, TbEdit, TbPin, TbPinFilled, TbTrash } from "solid-icons/tb";
import { type Component, createSignal, Show } from "solid-js";
import { useI18n } from "~/i18n/context";
import type { MemoryEntry as MemoryEntryType } from "~/wire/services/MemoryService";
import { ArchiveMemoryModal } from "./ArchiveMemoryModal";
import { DeleteMemoryModal } from "./DeleteMemoryModal";

interface MemoryEntryProps {
  entry: MemoryEntryType;
  onEdit: (id: string, content: string) => void;
  onArchive: (id: string, reason: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onPin: (id: string) => void;
  onUnpin: (id: string) => void;
  canEdit: boolean;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
}

export const MemoryEntry: Component<MemoryEntryProps> = (props) => {
  const { t } = useI18n();
  const [editing, setEditing] = createSignal(false);
  const [editContent, setEditContent] = createSignal("");
  const [archiveModalOpen, setArchiveModalOpen] = createSignal(false);
  const [deleteModalOpen, setDeleteModalOpen] = createSignal(false);

  const startEdit = () => {
    setEditContent(props.entry.content);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditContent("");
  };

  const saveEdit = () => {
    const trimmed = editContent().trim();
    if (!trimmed) return;
    props.onEdit(props.entry.id, trimmed);
    setEditing(false);
    setEditContent("");
  };

  const formattedDate = () => {
    const date = new Date(props.entry.createdAt);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <>
      <div
        class={clsx(
          "rounded-xl border border-primary-border bg-panel p-4",
          props.entry.pinned && "border-l-2 border-l-brand"
        )}
      >
        <Show
          when={!editing()}
          fallback={
            <div>
              <textarea
                value={editContent()}
                onInput={(e) => setEditContent(e.currentTarget.value)}
                class="h-24 w-full resize-none rounded-lg border border-muted-border bg-canvas p-3 text-sm text-primary-text focus:border-brand focus:ring-2 focus:ring-brand focus:outline-none"
              />
              <div class="mt-3 flex items-center justify-end gap-2">
                <StButton onClick={cancelEdit} class="px-3 py-1.5 text-sm text-secondary-text hover:text-primary-text">
                  {t("common.cancel")}
                </StButton>
                <StButton
                  onClick={saveEdit}
                  disabled={!editContent().trim()}
                  class="rounded-lg bg-brand px-3 py-1.5 text-sm text-on-brand hover:bg-brand-hover disabled:opacity-50"
                >
                  {t("common.save")}
                </StButton>
              </div>
            </div>
          }
        >
          <div class="flex items-start justify-between gap-3">
            <Show when={props.onToggleSelect}>
              <input
                type="checkbox"
                checked={props.selected ?? false}
                onChange={() => props.onToggleSelect?.(props.entry.id)}
                class="mt-1 size-4 shrink-0 cursor-pointer rounded border-muted-border accent-brand"
              />
            </Show>
            <div class="min-w-0 flex-1">
              <p class="text-sm wrap-break-word whitespace-pre-wrap text-primary-text">{props.entry.content}</p>
              <p class="mt-2 text-xs text-muted-text">{formattedDate()}</p>
            </div>
            <Show when={props.canEdit}>
              <div class="flex shrink-0 items-center gap-1">
                <StButton
                  onClick={() => (props.entry.pinned ? props.onUnpin(props.entry.id) : props.onPin(props.entry.id))}
                  class={
                    props.entry.pinned
                      ? "rounded-lg p-1.5 text-brand transition-colors hover:bg-canvas-hover hover:text-brand-hover"
                      : "rounded-lg p-1.5 text-muted-text transition-colors hover:bg-canvas-hover hover:text-primary-text"
                  }
                  aria-label={props.entry.pinned ? "Unpin memory" : "Pin memory"}
                >
                  <Show when={props.entry.pinned} fallback={<TbPin class="size-4" />}>
                    <TbPinFilled class="size-4" />
                  </Show>
                </StButton>
                <StButton
                  onClick={startEdit}
                  class="rounded-lg p-1.5 text-muted-text transition-colors hover:bg-canvas-hover hover:text-primary-text"
                  aria-label="Edit memory"
                >
                  <TbEdit class="size-4" />
                </StButton>
                <StButton
                  onClick={() => setArchiveModalOpen(true)}
                  class="rounded-lg p-1.5 text-muted-text transition-colors hover:bg-canvas-hover hover:text-amber-600"
                  aria-label="Archive memory"
                >
                  <TbArchive class="size-4" />
                </StButton>
                <StButton
                  onClick={() => setDeleteModalOpen(true)}
                  class="rounded-lg p-1.5 text-muted-text transition-colors hover:bg-canvas-hover hover:text-red-600"
                  aria-label="Delete memory"
                >
                  <TbTrash class="size-4" />
                </StButton>
              </div>
            </Show>
          </div>
        </Show>
      </div>

      <ArchiveMemoryModal
        isOpen={archiveModalOpen()}
        onClose={() => setArchiveModalOpen(false)}
        onConfirm={(reason) => props.onArchive(props.entry.id, reason)}
        memoryContent={props.entry.content}
      />

      <DeleteMemoryModal
        isOpen={deleteModalOpen()}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={() => props.onDelete(props.entry.id)}
        memoryContent={props.entry.content}
      />
    </>
  );
};
